import { writePsdBuffer } from 'ag-psd';
import JSZip from 'jszip';
import { ImageLayer, ImageLayerSet } from '../services/api/imageLayerService';

/**
 * レイヤー一式を PSD / ZIP に書き出す。
 *
 * サーバーを介さずブラウザで生成している。バナー程度のサイズなら十分速く、
 * PSD 生成のためだけに Lambda を用意する必要がないため。
 */

/** レイヤー画像を取得して canvas に描く。PSD もサムネイルも canvas 経由で作る */
const toCanvas = async (url: string): Promise<HTMLCanvasElement> => {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) {
    throw new Error(`レイヤー画像の取得に失敗しました (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);

  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas を初期化できませんでした');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
};

/** ファイル名に使えない文字を落とす */
const safeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80);

const sortedLayers = (set: ImageLayerSet): ImageLayer[] =>
  [...(set.layers ?? [])].sort((a, b) => a.z_index - b.z_index);

/**
 * レイヤー付き PSD を生成する。
 *
 * 制約: ag-psd はテキストレイヤーを書けないため、文字はラスタになる。
 * Photoshop で移動・差し替え・非表示はできるが、文字ツールでの打ち替えはできない。
 * その埋め合わせとして ZIP 側にテキスト一覧を同梱している。
 */
export const buildPsdBlob = async (set: ImageLayerSet): Promise<Blob> => {
  const layers = sortedLayers(set);
  if (layers.length === 0) {
    throw new Error('書き出すレイヤーがありません');
  }

  const children = [];
  for (const layer of layers) {
    children.push({
      name: layer.name,
      canvas: await toCanvas(layer.url),
      opacity: 1,
      hidden: false,
    });
  }

  const width = set.canvas_width ?? children[0].canvas.width;
  const height = set.canvas_height ?? children[0].canvas.height;

  const buffer = writePsdBuffer({ width, height, children }, { generateThumbnail: true });
  return new Blob([buffer], { type: 'image/vnd.adobe.photoshop' });
};

/**
 * レイヤー個別 PNG + テキスト一覧を ZIP にまとめる。
 * PSD が開けない環境でも中身を使えるようにするための併用手段。
 */
export const buildZipBlob = async (set: ImageLayerSet): Promise<Blob> => {
  const layers = sortedLayers(set);
  if (layers.length === 0) {
    throw new Error('書き出すレイヤーがありません');
  }

  const zip = new JSZip();
  const texts: string[] = [];

  // entries() の for...of は --downlevelIteration を要求されるため添字で回す
  for (let index = 0; index < layers.length; index += 1) {
    const layer = layers[index];
    const res = await fetch(layer.url, { mode: 'cors' });
    if (!res.ok) {
      throw new Error(`レイヤー画像の取得に失敗しました (HTTP ${res.status})`);
    }
    const order = String(index).padStart(2, '0');
    zip.file(`${order}_${safeFileName(layer.name)}.png`, await res.blob());

    if (layer.text_content) {
      texts.push(`[${order}] ${layer.name}\n${layer.text_content}\n`);
    }
  }

  // PSD の文字が打ち替えられない分、本文をコピペできる形で添える
  if (texts.length > 0) {
    zip.file('テキスト一覧.txt', texts.join('\n'));
  }

  return zip.generateAsync({ type: 'blob' });
};

/** Blob をダウンロードさせる */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // revoke を遅らせないと Safari でダウンロードが始まらないことがある
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
